import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../shared/api/errors';
import { Button } from '../../../shared/ui/button';
import { FieldLabel } from '../../../shared/ui/field-label';
import { Input } from '../../../shared/ui/input';
import { Banner } from '../../../shared/ui/layout';
import { Modal } from '../../../shared/ui/modal';
import { duplicatePreset, slugKey, type TAuthPreset } from '../../../entities/auth-preset';

export const PresetDuplicate = ({
  preset,
  label = 'Duplicate and edit the copy',
  onDuplicated,
}: {
  preset: TAuthPreset;
  label?: string;
  onDuplicated: (copy: TAuthPreset) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(`${preset.name} copy`);
  const queryClient = useQueryClient();

  const run = useMutation({
    mutationFn: () => duplicatePreset(preset.id, { name, key: slugKey(name) }),
    onSuccess: (copy) => {
      void queryClient.invalidateQueries({ queryKey: ['auth-presets'] });
      setOpen(false);
      onDuplicated(copy);
    },
  });

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        {label}
      </Button>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={`Duplicate ${preset.name}`}
        description="The copy belongs to this organization and is yours to change. The original keeps shipping with the product."
        submit={{
          label: 'Duplicate',
          onSubmit: () => run.mutate(),
          disabled: !name || run.isPending,
          waiting: run.isPending,
        }}
      >
        <div className="flex flex-col gap-3">
          <div>
            <FieldLabel htmlFor="duplicate-name">Name for the copy</FieldLabel>
            <Input
              id="duplicate-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <p className="mt-label font-mono text-micro text-faint">key: {slugKey(name)}</p>
          </div>
          {run.isError && (
            <Banner detail={run.error instanceof ApiError ? run.error.code : undefined}>
              {run.error instanceof Error ? run.error.message : 'The copy was refused.'}
            </Banner>
          )}
        </div>
      </Modal>
    </>
  );
};
